/* eslint-disable camelcase */
import { useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import PilotsTable from './components/PilotsTable'
import { formatPilotDateLocale, getDefaultPilotEndDate, getMinPilotEndDate } from './utils'

import styles from './PilotsPage.module.scss'

import { isApiError } from '@/api'
import { adminLookupUser, AdminUserLookupResponse, createEnterprisePilot } from '@/api/internal'
import { createUser } from '@/api/user'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import { usePricing } from '@/hooks/usePricing'
import { selectIsInternalAdmin } from '@/store/features/user/userSlice'
import { ProductSeatType } from '@/types'
import { DEFAULT_SEAT_TYPE_HINT, DEFAULT_SEAT_TYPE_OPTIONS, seatTypeLabel } from '@/utils/seatType'

const pilotSchema = z.object({
  email: z.string().email(),
  domain: z.string().min(1, 'Domain cannot be empty'),
  enterprise_name: z.string().min(1, 'Enterprise name cannot be empty'),
  trial_end_date: z.string().min(1, 'Trial end date is required'),
  default_seat_type: z.enum([ProductSeatType.Dictation, ProductSeatType.Bundle]),
  // Only shown (and required) when the admin email has no Wispr account yet.
  admin_first_name: z.string(),
  admin_last_name: z.string(),
})

type PilotFormValues = z.infer<typeof pilotSchema>

const normalizeEmail = (email: string) => email.trim().toLowerCase()

// A null result means "no Wispr account exists for this email".
const adminLookupQueryOptions = (email: string) => ({
  queryKey: ['pilot-admin-lookup', email] as const,
  queryFn: async (): Promise<AdminUserLookupResponse | null> => {
    try {
      return (await adminLookupUser(email)).data
    } catch (error) {
      if (isApiError(error) && error.status === 404) return null
      throw error
    }
  },
  retry: false,
  staleTime: 60_000,
})

const withPhase = (phase: string, error: unknown) =>
  new Error(isApiError(error) ? `${phase}: ${error.detail ?? error.message}` : phase)

const PilotsPage = () => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const { locale } = usePricing()
  const { updateToast } = useToast()
  const queryClient = useQueryClient()

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<PilotFormValues | null>(null)
  // Whether confirming the pending pilot will also create the admin account.
  const [pendingCreatesAccount, setPendingCreatesAccount] = useState(false)
  // Last admin email checked (on blur or submit), and the account we already
  // created, so a failed pilot step can be retried without a duplicate account.
  const [checkEmail, setCheckEmail] = useState<string | null>(null)
  const [createdEmail, setCreatedEmail] = useState<string | null>(null)

  // The resolver reads this through a ref so validation sees the current value.
  const creatingAdminRef = useRef(false)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PilotFormValues>({
    resolver: useMemo(
      () =>
        zodResolver(
          pilotSchema.superRefine((data, ctx) => {
            if (!creatingAdminRef.current) return
            if (!data.admin_first_name.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['admin_first_name'],
                message: 'First name is required to create this user',
              })
            }
            if (!data.admin_last_name.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['admin_last_name'],
                message: 'Last name is required to create this user',
              })
            }
          })
        ),
      []
    ),
    defaultValues: {
      trial_end_date: getDefaultPilotEndDate(),
      default_seat_type: ProductSeatType.Dictation,
      admin_first_name: '',
      admin_last_name: '',
    },
  })

  const emailValue = normalizeEmail(watch('email') ?? '')

  const lookupQuery = useQuery({
    ...adminLookupQueryOptions(checkEmail ?? ''),
    enabled: checkEmail !== null,
  })

  const creatingAdmin =
    checkEmail === emailValue && lookupQuery.isSuccess && lookupQuery.data === null && createdEmail !== emailValue
  creatingAdminRef.current = creatingAdmin

  const handleEmailBlur = () => {
    if (pilotSchema.shape.email.safeParse(emailValue).success) setCheckEmail(emailValue)
  }

  const createPilotMutation = useMutation({
    mutationFn: async (data: PilotFormValues) => {
      const email = normalizeEmail(data.email)
      const createAccount = pendingCreatesAccount && createdEmail !== email
      if (createAccount) {
        try {
          await createUser({
            email,
            first_name: data.admin_first_name.trim(),
            last_name: data.admin_last_name.trim(),
          })
        } catch (error) {
          throw withPhase('Failed to create the admin account', error)
        }
        setCreatedEmail(email)
      }
      // Send the canonical stored email: the backend resolves it with a
      // case-sensitive query, so raw input like Admin@Corp.com would 404.
      const lookup = queryClient.getQueryData<AdminUserLookupResponse | null>(adminLookupQueryOptions(email).queryKey)
      const payload = {
        email: lookup?.email ?? email,
        domain: data.domain,
        enterprise_name: data.enterprise_name,
        trial_end: data.trial_end_date,
        default_seat_type: data.default_seat_type,
      }
      try {
        try {
          return await createEnterprisePilot(payload)
        } catch (error) {
          // A just-created account exists on the writer DB while create-pilot
          // resolves the admin from a read replica, so an immediate 404 is
          // likely replication lag: retry once (same reasoning as the delayed
          // invalidateQueries in onSuccess).
          if (!createAccount || !isApiError(error) || error.status !== 404) throw error
          await new Promise((resolve) => setTimeout(resolve, 1500))
          return await createEnterprisePilot(payload)
        }
      } catch (error) {
        throw createAccount ? withPhase('Account created, but pilot creation failed', error) : error
      }
    },
    onSuccess: () => {
      updateToast('Pilot created successfully', 'success')
      setShowConfirmDialog(false)
      setPendingFormData(null)
      setPendingCreatesAccount(false)
      // The pilots list reads from a replica, so wait briefly for this write to replicate.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['enterprise-pilots'] })
      }, 1000)
    },
    onError: (error) => {
      console.error('Failed to create pilot:', error)
      setShowConfirmDialog(false)
      const message = isApiError(error) ? error.detail : error.message
      updateToast(message || 'Failed to create pilot', 'error')
    },
  })

  // Authorization: only admin users can access this page
  if (!isInternalAdmin) {
    return (
      <div className={styles.container}>
        <p>You are not authorized to access this page</p>
      </div>
    )
  }

  const onSubmit = () => {
    if (pendingFormData) {
      createPilotMutation.mutate(pendingFormData)
    }
  }

  const handleCancelCreate = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
    setPendingCreatesAccount(false)
  }

  const handleCreatePilot = handleSubmit(async (data) => {
    const email = normalizeEmail(data.email)
    let lookup: AdminUserLookupResponse | null
    try {
      setCheckEmail(email)
      // Shares the cache (and any in-flight request) with the blur lookup.
      lookup = await queryClient.fetchQuery(adminLookupQueryOptions(email))
    } catch (error) {
      updateToast(isApiError(error) ? (error.detail ?? error.message) : 'Failed to look up user', 'error')
      return
    }
    const createAccount = lookup === null && createdEmail !== email
    if (createAccount && (!data.admin_first_name.trim() || !data.admin_last_name.trim())) {
      // This email just turned out to be missing: the name fields are now
      // revealed, and the resolver enforces them on the next submit.
      return
    }
    setPendingCreatesAccount(createAccount)
    setPendingFormData(data)
    setShowConfirmDialog(true)
  })

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Create Pilot Program</h1>

      {/* Confirmation Dialog */}
      <DialogWrapper
        isOpen={showConfirmDialog}
        title="Confirm pilot creation"
        subtitle="You are about to give this organization Enterprise access until the trial end date"
        close={handleCancelCreate}
        minWidth={540}
      >
        <div className={styles.dialogContent}>
          {pendingFormData && (
            <div className={styles.dialogDetailsSection}>
              <p className={styles.dialogSectionTitle}>Pilot Details</p>
              <div className={styles.dialogNotificationDetails}>
                <p className={styles.dialogDetailItem}>
                  <strong>Admin Email:</strong>
                  <span>{pendingFormData.email}</span>
                </p>
                {pendingCreatesAccount && (
                  <p className={styles.dialogDetailItem}>
                    <strong>New Account:</strong>
                    <span>
                      {pendingFormData.admin_first_name} {pendingFormData.admin_last_name} — this email has no Wispr
                      account; one will be created and sent a password-setup email
                    </span>
                  </p>
                )}
                <p className={styles.dialogDetailItem}>
                  <strong>Domain:</strong>
                  <span>{pendingFormData.domain}</span>
                </p>
                <p className={styles.dialogDetailItem}>
                  <strong>Enterprise Name:</strong>
                  <span>{pendingFormData.enterprise_name}</span>
                </p>
                <p className={styles.dialogDetailItem}>
                  <strong>Trial End Date:</strong>
                  <span>{formatPilotDateLocale(pendingFormData.trial_end_date, locale)}</span>
                </p>
                <p className={styles.dialogDetailItem}>
                  <strong>Default Seat Type:</strong>
                  <span>{seatTypeLabel[pendingFormData.default_seat_type]}</span>
                </p>
              </div>
              <div className={styles.dialogWarningMessage}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>
                  Access ends automatically on the end date unless you extend or convert the pilot first. Nothing is
                  created in Stripe.
                </span>
              </div>
            </div>
          )}
          <div className={styles.dialogActions}>
            <Button
              title="Cancel"
              onClick={handleCancelCreate}
              type="light"
              isDisabled={createPilotMutation.isPending}
            />
            <Button
              title={createPilotMutation.isPending ? 'Creating...' : 'Create Pilot'}
              onClick={onSubmit}
              type="dark"
              isLoading={createPilotMutation.isPending}
              isDisabled={createPilotMutation.isPending}
            />
          </div>
        </div>
      </DialogWrapper>

      <form className={styles.formContainer} onSubmit={(e) => e.preventDefault()}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Pilot Information</h2>

          <div className={styles.formGroup} onBlur={handleEmailBlur}>
            <label className={styles.label}>Email *</label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  type="email"
                  value={field.value}
                  onInput={field.onChange}
                  placeholder="contact@company.com"
                  className={styles.input}
                />
              )}
            />
            {errors.email && <p className={styles.errorText}>{errors.email.message}</p>}
            {lookupQuery.isFetching && <p className={styles.helperText}>Checking account…</p>}
          </div>

          {creatingAdmin && (
            <>
              <p className={styles.warningMessage}>
                No Wispr account exists for <strong>{emailValue}</strong>. Enter a name and one will be created (the
                user receives a password-setup email).
              </p>
              <div className={styles.formGroup}>
                <label className={styles.label}>First Name *</label>
                <Controller
                  name="admin_first_name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="text"
                      value={field.value}
                      onInput={field.onChange}
                      placeholder="Jane"
                      className={styles.input}
                    />
                  )}
                />
                {errors.admin_first_name && <p className={styles.errorText}>{errors.admin_first_name.message}</p>}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Last Name *</label>
                <Controller
                  name="admin_last_name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="text"
                      value={field.value}
                      onInput={field.onChange}
                      placeholder="Doe"
                      className={styles.input}
                    />
                  )}
                />
                {errors.admin_last_name && <p className={styles.errorText}>{errors.admin_last_name.message}</p>}
              </div>
            </>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>Domain *</label>
            <Controller
              name="domain"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  value={field.value}
                  onInput={field.onChange}
                  placeholder="company.com"
                  className={styles.input}
                />
              )}
            />
            {errors.domain && <p className={styles.errorText}>{errors.domain.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Enterprise Name *</label>
            <Controller
              name="enterprise_name"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  value={field.value}
                  onInput={field.onChange}
                  placeholder="Acme Corporation"
                  className={styles.input}
                />
              )}
            />
            {errors.enterprise_name && <p className={styles.errorText}>{errors.enterprise_name.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Trial End Date * <span className={styles.helperText}>(select a future date)</span>
            </label>
            <Controller
              name="trial_end_date"
              control={control}
              render={({ field }) => (
                <input
                  type="date"
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  className={styles.input}
                  min={getMinPilotEndDate()}
                />
              )}
            />
            {errors.trial_end_date && <p className={styles.errorText}>{errors.trial_end_date.message}</p>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="pilot-default-seat-type">
              Default Seat Type *
            </label>
            <Controller
              name="default_seat_type"
              control={control}
              render={({ field }) => (
                <Select
                  id="pilot-default-seat-type"
                  value={field.value}
                  options={DEFAULT_SEAT_TYPE_OPTIONS}
                  onChange={field.onChange}
                />
              )}
            />
            <p className={styles.helperText}>{DEFAULT_SEAT_TYPE_HINT} Ignored if the organization already exists.</p>
          </div>
        </div>

        <div className={styles.actionContainer}>
          <Button
            title={creatingAdmin ? 'Create User & Pilot' : 'Create Pilot'}
            onClick={handleCreatePilot}
            type="dark"
            isLoading={isSubmitting}
            isDisabled={isSubmitting || createPilotMutation.isPending || lookupQuery.isFetching}
          />
        </div>
      </form>

      <PilotsTable />
    </div>
  )
}

export default PilotsPage
